// scheduleUtils.js — date maths, colours, session types and conflict detection
// shared by the roster and timeline views. Dates are 'YYYY-MM-DD' strings and
// times 'HH:MM' strings throughout, built from LOCAL date parts so nothing
// shifts across timezones.

const pad = (n) => String(n).padStart(2, '0');

export const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const fromISO = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (iso, n) => {
  const d = fromISO(iso);
  d.setDate(d.getDate() + n);
  return toISO(d);
};
export const todayISO = () => toISO(new Date());
// Weeks start on Monday.
export const startOfWeek = (iso) => addDays(iso, -((fromISO(iso).getDay() + 6) % 7));
export const weekDays = (start) => Array.from({ length: 7 }, (_, i) => addDays(start, i));
export const isWeekend = (iso) => [0, 6].includes(fromISO(iso).getDay());

export const fmtDayName = (iso) => fromISO(iso).toLocaleDateString('en-ZA', { weekday: 'short' });
export const fmtDayNum = (iso) => fromISO(iso).getDate();
export const fmtLongDay = (iso) =>
  fromISO(iso).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
export const fmtWeekRange = (start) => {
  const a = fromISO(start);
  const b = fromISO(addDays(start, 6));
  const month = (d) => d.toLocaleDateString('en-ZA', { month: 'short' });
  const left = a.getMonth() === b.getMonth() ? `${a.getDate()}` : `${a.getDate()} ${month(a)}`;
  return `${left} – ${b.getDate()} ${month(b)} ${b.getFullYear()}`;
};

export const toMin = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
export const fromMin = (min) => {
  const clamped = Math.max(0, Math.min(min, 23 * 60 + 59));
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
};

// ---------------------------------------------------------------------------
// Session types
// ---------------------------------------------------------------------------
export const BLOCK_TYPES = {
  theatre: { label: 'Theatre list', short: 'Theatre', timed: true },
  rooms: { label: 'Rooms / consults', short: 'Rooms', timed: true },
  on_call: { label: 'On call', short: 'On call', timed: false },
  leave: { label: 'Leave / away', short: 'Leave', timed: false },
};
export const TYPE_ORDER = ['theatre', 'rooms', 'on_call', 'leave'];

// One-click time presets per type — the common anaesthetic sessions.
export const PRESETS = {
  theatre: [
    { label: 'Morning', start: '07:00', end: '13:00' },
    { label: 'Afternoon', start: '13:00', end: '18:00' },
    { label: 'Full day', start: '07:00', end: '17:00' },
  ],
  rooms: [
    { label: 'Morning', start: '08:00', end: '12:00' },
    { label: 'Afternoon', start: '13:00', end: '17:00' },
  ],
  on_call: [
    { label: 'Night', start: '18:00', end: '07:00' },
    { label: '24 hours', start: '07:00', end: '07:00' },
  ],
  leave: [],
};

export const timeLabel = (b) => {
  if (b.btype === 'leave') return 'All day';
  if (!b.bstart) return 'All day';
  const overnight = b.btype === 'on_call' && toMin(b.bend) <= toMin(b.bstart);
  return `${b.bstart}–${b.bend}${overnight ? ' (+1)' : ''}`;
};

// ---------------------------------------------------------------------------
// Doctor colours — assigned by position in the uid-ordered doctor list, so a
// doctor keeps the same colour in every view and on the mobile app.
// ---------------------------------------------------------------------------
export const PALETTE = ['#2563eb', '#0d9488', '#db2777', '#d97706', '#7c3aed', '#16a34a', '#dc2626', '#0891b2'];
export const colourMap = (doctors) =>
  Object.fromEntries(doctors.map((d, i) => [d.uid, PALETTE[i % PALETTE.length]]));

export const doctorName = (d) => d?.ufull_name || d?.uname || 'Unknown';
export const initials = (d) =>
  doctorName(d)
    .replace(/^dr\.?\s+/i, '')
    .split(/[\s.]+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(-2)
    .join('')
    .toUpperCase();

export const hospitalLabel = (h) => h?.hshort || h?.hname || '';

// Sort within a day: all-day items first, then by start time.
export const byTime = (a, b) => (toMin(a.bstart) ?? -1) - (toMin(b.bstart) ?? -1) || a.bid - b.bid;

// ---------------------------------------------------------------------------
// Conflicts — for one doctor on one day: two timed sessions that overlap, or
// any session on a day they're on leave. On call alongside a list is normal.
// Returns a Map bid -> reason.
// ---------------------------------------------------------------------------
export const findConflicts = (blocks) => {
  const out = new Map();
  const groups = {};
  blocks.forEach((b) => {
    const k = `${b.buid}|${b.bdate}`;
    (groups[k] = groups[k] || []).push(b);
  });
  Object.values(groups).forEach((list) => {
    const leave = list.some((b) => b.btype === 'leave');
    const timed = list.filter((b) => BLOCK_TYPES[b.btype].timed).sort(byTime);
    if (leave) {
      list.filter((b) => b.btype !== 'leave').forEach((b) => out.set(b.bid, 'Doctor is on leave this day'));
    }
    for (let i = 0; i < timed.length; i += 1) {
      for (let j = i + 1; j < timed.length; j += 1) {
        if (toMin(timed[j].bstart) < toMin(timed[i].bend)) {
          out.set(timed[i].bid, 'Overlaps another session');
          out.set(timed[j].bid, 'Overlaps another session');
        }
      }
    }
  });
  return out;
};

// Check a draft (not yet saved) against existing blocks — used live in the editor.
export const draftConflict = (draft, dates, blocks) => {
  for (const date of dates) {
    const others = blocks.filter((b) => b.buid === draft.buid && b.bdate === date && b.bid !== draft.bid);
    if (draft.btype !== 'leave' && others.some((b) => b.btype === 'leave')) {
      return `On leave on ${fmtLongDay(date)}.`;
    }
    if (draft.btype === 'leave' && others.length > 0) {
      return `Already has ${others.length} session${others.length > 1 ? 's' : ''} on ${fmtLongDay(date)}.`;
    }
    if (BLOCK_TYPES[draft.btype].timed && draft.bstart && draft.bend) {
      const s = toMin(draft.bstart);
      const e = toMin(draft.bend);
      const clash = others.find((b) => BLOCK_TYPES[b.btype].timed && toMin(b.bstart) < e && s < toMin(b.bend));
      if (clash) return `Overlaps a ${timeLabel(clash)} session on ${fmtLongDay(date)}.`;
    }
  }
  return '';
};

// ---------------------------------------------------------------------------
// Timeline layout — place overlapping blocks side by side (like a calendar
// app). Returns [{ block, col, cols }].
// ---------------------------------------------------------------------------
export const layoutDay = (blocks) => {
  const sorted = [...blocks].sort((a, b) => toMin(a.bstart) - toMin(b.bstart) || toMin(b.bend) - toMin(a.bend));
  const placed = [];
  let cluster = [];
  let clusterEnd = -1;

  const flush = () => {
    const cols = [];
    cluster.forEach((item) => {
      let c = cols.findIndex((end) => end <= toMin(item.block.bstart));
      if (c === -1) {
        c = cols.length;
        cols.push(0);
      }
      cols[c] = toMin(item.block.bend);
      item.col = c;
    });
    cluster.forEach((item) => placed.push({ ...item, cols: cols.length }));
    cluster = [];
  };

  sorted.forEach((block) => {
    if (cluster.length && toMin(block.bstart) >= clusterEnd) {
      flush();
      clusterEnd = -1;
    }
    cluster.push({ block, col: 0 });
    clusterEnd = Math.max(clusterEnd, toMin(block.bend));
  });
  flush();
  return placed;
};

// Translucent tint of a hex colour for block backgrounds.
export const tint = (hex, alpha = 0.12) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
